import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import * as R from 'ramda';

import {
  INITIAL_LIMIT,
  INITIAL_PAGE,
} from '@atom/components/common/requests/assetRequestConstants';
import {
  cleanFilters,
  getCustomValues,
  getTypeChangeFilters,
} from '@atom/components/common/requests/customTenantUtilities';
import RequestsTable from '@atom/components/common/requests/requestsTable/RequestsTable';
import ViewRequestModal from '@atom/components/common/requests/viewRequest/ViewRequestModal';
import { GET_ASSET_REQUESTS } from '@atom/graph/assetRequest';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, Progress } from '@atom/mui';
import { ArrowBackIcon } from '@atom/mui/icons';
import colors from '@atom/styles/colors';
import { AssetDetailView } from '@atom/types/asset';
import {
  AssetRequest,
  AssetRequestsFilters,
  AssetRequestType,
} from '@atom/types/assetRequest';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import RequestsFilters from './requestsFilters/RequestsFilters';
import RequestsContext from './RequestsContext';

import classes from './requests.module.css';

const RequestsPortal = () => {
  const [, { backDestination, navigate }] = useNavigation();
  const { initialFilters, initialSort } = getCustomValues();

  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [limit, setLimit] = useState<number>(INITIAL_LIMIT);
  const [sortBy, setSortBy] = useState<string>(initialSort.sortBy);
  const [isAscending, setIsAscending] = useState<boolean>(
    initialSort.isAscending,
  );
  const [filters, setFilters] = useState<AssetRequestsFilters>({});
  const [filtersCart, setFiltersCart] = useState<AssetRequestsFilters>({});
  const [activeRequestId, setActiveRequestId] = useState<string>();

  const resetInitialValues = () => {
    setPage(INITIAL_PAGE);
    setLimit(INITIAL_LIMIT);
    setSortBy(initialSort.sortBy);
    setIsAscending(initialSort.isAscending);
  };

  const resetFilters = () => {
    resetInitialValues();
    setFilters(initialFilters);
    setFiltersCart(initialFilters);
  };

  useEffect(() => {
    if (
      isNilOrEmpty(filters) &&
      isNilOrEmpty(filtersCart) &&
      !isNilOrEmpty(initialFilters)
    ) {
      resetFilters();
    }
  }, [filters, filtersCart, initialFilters]);

  const {
    data,
    loading: assetRequestsLoading,
    refetch: refetchAssetRequests,
  } = useQuery(GET_ASSET_REQUESTS, {
    variables: {
      input: {
        page,
        limit,
        sortBy,
        isAscending,
        ...(R.pathOr([], ['schemaIds'], filters)[0] !== '' && {
          schemaIds: filters.schemaIds,
        }),
        ...R.omit(['schemaIds'], filters),
      },
    },
    fetchPolicy: 'network-only',
  });

  const navigateBack = (): void => {
    navigate(backDestination('/inventory'));
  };

  const navigateToReplenishedItem = (request: AssetRequest): void => {
    navigate(
      `/inventory/${request.assetId}?activeView=${AssetDetailView.REQUESTS}&activeRequestId=${request.id}`,
    );
  };

  // When type is changed, filters should be automatically applied.
  const changeType = (type: AssetRequestType) => {
    const updatedFilter = getTypeChangeFilters(type);

    // Handles refetching if a new request is created without changing types.
    if (type === filters?.type) {
      refetchAssetRequests();
    }

    setFiltersCart(updatedFilter);
    setFilters(cleanFilters(updatedFilter));
  };

  const applyFilters = () => {
    resetInitialValues();
    setFilters(cleanFilters(filtersCart));
  };

  const totalCount = R.pathOr(0, ['assetRequests', 'totalCount'], data);
  const requests = R.pathOr([], ['assetRequests', 'assetRequests'], data);

  return (
    <RequestsContext.Provider
      value={{
        requests,
        totalCount,
        filters,
        filtersCart,
        setFiltersCart,
        page,
        setPage,
        limit,
        setLimit,
        sortBy,
        setSortBy,
        isAscending,
        setIsAscending,
        changeType,
        setActiveRequestId,
      }}
    >
      <>
        <div className={classes.headerContainer}>
          <div className={classes.headerButtons}>
            <IconButton>
              <Icon
                color={colors.neutral.white}
                onClick={navigateBack}
                icon={ArrowBackIcon}
              />
            </IconButton>
            <div>Inventory Requests</div>
          </div>
        </div>
        <div className={classes.bodyContainer}>
          <div className={classes.leftBodyPaneFixed}>
            <RequestsFilters
              applyFilters={applyFilters}
              resetFilters={resetFilters}
            />
          </div>
          <div className={classes.rightBodyPaneFill}>
            {assetRequestsLoading ? (
              <div className={classes.progressContainer}>
                <Progress />
              </div>
            ) : (
              <RequestsTable
                requests={requests}
                totalCount={totalCount}
                filters={filters}
                page={page}
                setPage={setPage}
                limit={limit}
                setLimit={setLimit}
                sortBy={sortBy}
                setSortBy={setSortBy}
                isAscending={isAscending}
                setIsAscending={setIsAscending}
                setActiveRequestId={setActiveRequestId}
                offsetTop="165px"
                isPortalView
              />
            )}
          </div>
        </div>
        <ViewRequestModal
          activeRequestId={activeRequestId}
          setActiveRequestId={setActiveRequestId}
          onReplenishmentSaved={navigateToReplenishedItem}
        />
      </>
    </RequestsContext.Provider>
  );
};

export default RequestsPortal;
